import { Component, DestroyRef, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { of, switchMap } from 'rxjs';
import { ApplicationFormService } from '../../../services/application-form/application-form.service';
import { InstitutionService } from '../../../services/institution/institution.service';
import { ProductService } from '../../../services/products/product.service';
import { ToastService } from '../../../services/general/toast.service';
import { FieldOption, FormFieldConfig, FormFieldType } from '../../../interfaces/responses/application-form.interface';
import { Institution } from '../../../interfaces/responses/institution.interface';
import { Product } from '../../../interfaces/responses/product-response.interfaces';
import { ProgressLoaderComponent } from '../../../components/loaders/progress-loader/progress-loader.component';

@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ProgressLoaderComponent],
  templateUrl: './form-builder.component.html',
  styleUrl: './form-builder.component.scss',
})
export class FormBuilderComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _fb = inject(FormBuilder);
  private readonly _formService = inject(ApplicationFormService);
  private readonly _institutionService = inject(InstitutionService);
  private readonly _productService = inject(ProductService);
  private readonly _toast = inject(ToastService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _platformId = inject(PLATFORM_ID);

  readonly isEditMode = signal(false);
  readonly formId = signal<string | null>(null);
  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly isLoadingFields = signal(false);
  readonly defaultFieldsError = signal(false);

  readonly institutions = signal<Institution[]>([]);
  readonly products = signal<Product[]>([]);
  readonly fields = signal<FormFieldConfig[]>([]);

  readonly savedToken = signal<string | null>(null);
  readonly showSuccessDialog = signal(false);

  readonly fieldTypes: FormFieldType[] = ['TEXT', 'EMAIL', 'PHONE', 'NUMBER', 'DATE', 'SELECT', 'MULTI_SELECT', 'FILE', 'BOOLEAN', 'URL'];

  selectorForm!: FormGroup;
  addFieldForm!: FormGroup;
  newOptions: Record<string, FieldOption> = {};

  ngOnInit(): void {
    const id = this._route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditMode.set(true);
      this.formId.set(id);
    }

    this.selectorForm = this._fb.group({
      institutionId: ['', Validators.required],
      productId: ['', Validators.required],
      name: ['', Validators.required],
      description: [''],
    });

    this.addFieldForm = this._fb.group({
      label: ['', Validators.required],
      type: ['TEXT', Validators.required],
      required: [false],
      placeholder: [''],
    });

    this.loadInstitutions();

    this.selectorForm.get('institutionId')?.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((institutionId: string) => {
        this.selectorForm.patchValue({ productId: '' });
        if (institutionId) this.loadProducts(institutionId);
      });

    if (this.isEditMode() && this.formId()) {
      this.loadExistingForm(this.formId()!);
    } else {
      this.loadDefaultFields();
    }
  }

  loadInstitutions(): void {
    this._institutionService.getPaginatedInstitutions({ paginate: false }).subscribe({
      next: (res) => this.institutions.set(res.data ?? []),
      error: () => this._toast.error('Failed to load institutions'),
    });
  }

  loadProducts(institutionId: string): void {
    this._productService.getPaginatedProducts({ institutionId, paginate: false }).subscribe({
      next: (res) => this.products.set(res.data ?? []),
      error: () => this._toast.error('Failed to load products'),
    });
  }

  loadDefaultFields(): void {
    this.isLoadingFields.set(true);
    this.defaultFieldsError.set(false);
    this._formService.getDefaultFields().subscribe({
      next: (defaults) => {
        this.fields.set(defaults.map((f, i) => ({ ...f, isDefault: true, order: i })));
        this.isLoadingFields.set(false);
      },
      error: () => {
        this.defaultFieldsError.set(true);
        this.isLoadingFields.set(false);
        this._toast.error('Failed to load default fields');
      },
    });
  }

  loadExistingForm(id: string): void {
    this.isLoading.set(true);
    this._formService.getFormById(id).subscribe({
      next: (form) => {
        this.savedToken.set(form.token);
        this.selectorForm.patchValue({ institutionId: form.institutionId, name: form.name ?? '', description: form.description ?? '' }, { emitEvent: false });
        this._productService.getPaginatedProducts({ institutionId: form.institutionId, paginate: false }).subscribe({
          next: (prodRes) => {
            this.products.set(prodRes.data ?? []);
            this.selectorForm.patchValue({ productId: form.productId }, { emitEvent: false });
            this.fields.set(form.fields ?? []);
            this.isLoading.set(false);
          },
          error: () => {
            this._toast.error('Failed to load products');
            this.isLoading.set(false);
          },
        });
      },
      error: () => {
        this._toast.error('Form not found');
        this.isLoading.set(false);
        this._router.navigate(['/application-forms']);
      },
    });
  }

  toggleDefaultRequired(field: FormFieldConfig): void {
    this.fields.update(list =>
      list.map(f => f.key === field.key ? { ...f, required: !f.required } : f)
    );
  }

  addCustomField(): void {
    if (this.addFieldForm.invalid) return;
    const { label, type, required, placeholder } = this.addFieldForm.value;
    const key = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!key) {
      this._toast.error('Label must contain at least one letter or digit');
      return;
    }
    if (this.fields().some(f => f.key === key)) {
      this._toast.error('A field with this key already exists. Please use a different label.');
      return;
    }
    const newField: FormFieldConfig = {
      key,
      label,
      type,
      required,
      placeholder,
      isDefault: false,
      options: (type === 'SELECT' || type === 'MULTI_SELECT') ? [] : undefined,
      order: this.fields().length,
    };
    this.fields.update(list => [...list, newField]);
    this.addFieldForm.reset({ label: '', type: 'TEXT', required: false, placeholder: '' });
  }

  removeCustomField(key: string): void {
    this.fields.update(list => list.filter(f => f.key !== key).map((f, i) => ({ ...f, order: i })));
  }

  moveField(index: number, direction: 'up' | 'down'): void {
    const list = [...this.fields()];
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= list.length) return;
    [list[index], list[swap]] = [list[swap], list[index]];
    this.fields.set(list.map((f, i) => ({ ...f, order: i })));
  }

  getNewOption(fieldKey: string): FieldOption {
    if (!this.newOptions[fieldKey]) {
      this.newOptions[fieldKey] = { value: '', label: '' };
    }
    return this.newOptions[fieldKey];
  }

  addOption(fieldKey: string): void {
    const opt = this.newOptions[fieldKey];
    if (!opt?.value?.trim() || !opt?.label?.trim()) return;
    this.fields.update(list =>
      list.map(f => f.key === fieldKey
        ? { ...f, options: [...(f.options ?? []), { value: opt.value.trim(), label: opt.label.trim() }] }
        : f
      )
    );
    this.newOptions[fieldKey] = { value: '', label: '' };
  }

  removeOption(fieldKey: string, optionValue: string): void {
    this.fields.update(list =>
      list.map(f => f.key === fieldKey
        ? { ...f, options: (f.options ?? []).filter(o => o.value !== optionValue) }
        : f
      )
    );
  }

  canSave(): boolean {
    return this.selectorForm.valid && this.fields().length > 0 && !this.defaultFieldsError();
  }

  save(): void {
    if (!this.canSave()) return;
    this.isSaving.set(true);
    const { institutionId, productId, name, description } = this.selectorForm.value;

    if (this.isEditMode() && this.formId()) {
      const formId = this.formId()!;
      const customFields = this.fields().filter(f => !f.isDefault);
      const existingFields = customFields.filter(f => f.id);
      const newFields = customFields.filter(f => !f.id);

      const patch$ = existingFields.length
        ? this._formService.updateFormFields(formId, existingFields)
        : of(null);

      patch$.pipe(
        switchMap(() => newFields.length
          ? this._formService.createFormFields(formId, newFields)
          : of(null)
        ),
        takeUntilDestroyed(this._destroyRef),
      ).subscribe({
        next: () => {
          this.showSuccessDialog.set(true);
          this.isSaving.set(false);
        },
        error: () => {
          this._toast.error('Failed to save form');
          this.isSaving.set(false);
        },
      });
    } else {
      this._formService.createForm({ institutionId, productId, name, description }).pipe(
        switchMap(form => {
          this.savedToken.set(form.token);
          this.formId.set(form.id);
          this.isEditMode.set(true);
          return this._formService.createFormFields(form.id, this.fields());
        }),
        takeUntilDestroyed(this._destroyRef),
      ).subscribe({
        next: () => {
          this.showSuccessDialog.set(true);
          this.isSaving.set(false);
        },
        error: () => {
          this._toast.error('Failed to save form');
          this.isSaving.set(false);
        },
      });
    }
  }

  get shareableLink(): string {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/apply/${this.savedToken()}`;
  }

  copyShareableLink(): void {
    if (!isPlatformBrowser(this._platformId)) return;
    navigator.clipboard.writeText(this.shareableLink).then(() => {
      this._toast.success('Link copied to clipboard');
    }).catch(() => {
      this._toast.error('Failed to copy link');
    });
  }

  closeSuccessDialog(): void {
    this.showSuccessDialog.set(false);
    this._router.navigate(['/application-forms']);
  }

  goBack(): void {
    this._router.navigate(['/application-forms']);
  }
}
